export interface PopupCarouselItem {
  enabled: boolean;
  backgroundColor: string;
  textColor: string;
  title: string;
  description: string;
}

export enum PopupChildItem {
  LOGIN = "login",
}

export enum PopupWindow {
  PRE_LOGIN = "pre_login",
  POST_LOGIN = "post_login",
  USER_DETAILS = "user_details",
}

export type GoogleFontFaceOptionsBackend = {
  family_name: string;
  style: string;
  weight: string | number;
  display: string;
  unicode_range: string;
  src: string;
};

export type GoogleFontFaceOptions = {
  familyName: string;
  src: string;
  style: string;
  weight: string | number;
  display: string;
  unicodeRange: string;
};

export type CustomFontFaceOptions = string;

export type PreLoginContentAlignment = "left" | "center" | "right";

export interface PopupBackgroundGradient {
  enabled: boolean;
  colorFrom: string;
  colorTo: string;
  angleDeg: number;
  midStopPercent: number;
}

export interface SSOMarketingPopupBackendData {
  should_load_font_faces?: boolean;
  font_type?: "google" | "custom";
  google_font_faces?: GoogleFontFaceOptionsBackend[];
  custom_font_faces?: CustomFontFaceOptions[];
  logo_url: string;
  background_color: string;
  pre_login_content_alignment?: PreLoginContentAlignment;
  pop_trigger_time: number;
  border_radius?: string;
  primary_cta: {
    text: string;
    color: string;
    background_color: string;
  };
  title: {
    text: string;
    color: string;
  };
  sub_title?: {
    text: string;
    color: string;
  };
  banner: {
    enabled: boolean;
    background_color?: string;
    text_color?: string;
    items: {
      type: string;
      enabled: boolean;
      text_color: string;
      background_color?: string;
      html_text_string: string;
    }[];
  };
  card: {
    enabled: boolean;
    background_color: string;
    background_image?: string;
    text_color: string;
    border_color: string;
    border_width: string;
    border_radius: string;
    items: {
      background_image_fit: string;
      is_top_icon_text_or_image?: "text" | "image";
      top_icon_text?: string;
      top_icon_text_color?: string;
      top_icon_image?: string;
      title: string;
      title_color?: string;
      description?: string;
      description_color?: string;
      border_color?: string;
      border_width?: string;
      border_radius?: string;
      background_color?: string;
      background_image?: string;
    }[];
  };
  post_login?: {
    background_color: string;
    primary_cta: {
      text: string;
      color: string;
      background_color: string;
    };
    title: {
      text: string;
      color: string;
    };
    sub_title: {
      text: string;
      color: string;
    };
    discount_card_enabled: boolean;
  };
}

export interface PostLoginDiscountCardProps {
  code?: string;
  title?: string;
  description?: string;
  tagText?: string;
  tagBackgroundColor?: string;
  tagTextColor?: string;
  handleCopyCouponCode?: (code: string) => void;
}

export interface SSOMarketingPopup {
  shouldLoadFontFaces?: boolean;
  fontType?: "google" | "custom";
  // Merchant
  googleFontFaces?: GoogleFontFaceOptions[];
  customFontFaces?: CustomFontFaceOptions[];
  logoUrl: string;
  /** Solid hex or full `linear-gradient(...)` CSS for the popup shell `background`. */
  backgroundColor: string;
  /** Logo + pre-login title + subtitle alignment (single control in merchant setup). */
  preLoginContentAlignment?: PreLoginContentAlignment;
  /** @deprecated Legacy merchant gradient editor; shell uses solid `backgroundColor` only. */
  popupBackgroundGradient?: PopupBackgroundGradient;
  popTriggerTime: number;
  borderRadius?: string;

  // Primary CTA
  primaryCTA: {
    text: string;
    color: string;
    backgroundColor: string;
  };

  // Title
  title: {
    text: string;
    color: string;
  };

  // Subtitle
  subTitle?: {
    text: string;
    color: string;
  };

  // Banner
  banner: {
    enabled: boolean;
    backgroundColor?: string;
    textColor?: string;
    items: Array<{
      type: string;
      enabled: boolean;
      textColor: string;
      backgroundColor?: string;
      htmlTextString: string;
    }>;
  };

  // Card
  card: {
    enabled: boolean;
    backgroundColor: string;
    backgroundImage?: string;
    textColor: string;
    borderColor: string;
    borderWidth: string;
    borderRadius: string;
    items: Array<{
      backgroundImageFit: string;
      isTopIconTextOrImage?: "text" | "image";
      topIconText?: string;
      topIconTextColor?: string;
      topIconImage?: string;
      backgroundColor?: string;
      backgroundImage?: string;
      // Title
      title: string;
      titleColor?: string;
      // Description
      description?: string;
      descriptionColor?: string;
      // Border
      borderColor?: string;
      borderWidth?: string;
      borderRadius?: string;
    }>;
  };
  postLogin?: {
    backgroundColor: string;
    primaryCTA: {
      text: string;
      color: string;
      backgroundColor: string;
    };
    title: {
      text: string;
      color: string;
    };
    subtitle: {
      text: string;
      color: string;
    };
    discountCardEnabled: boolean;
  };
}

export type PopupProps = {
  popup: SSOMarketingPopup;
  fontFamily?: string;
  onClose: () => void;
  closeButtonPosition?: "top-right" | "top-center-outside";
  showCloseButton?: boolean;
  hidePreLoginDecorations?: boolean;
  overRides?: OverRides;
  children?: React.ReactNode;
  currentWindow: PopupWindow;
};

export enum DeviceType {
  MOBILE = "mobile",
  DESKTOP = "desktop",
}

export type OverRides = {
  isPreview?: boolean;
  /** When true with `closeButtonPosition="top-center-outside"`, the outside close renders in merchant preview (normally hidden in preview). */
  renderOutsideCloseInPreview?: boolean;
  device?: DeviceType;
  focusedComponent?: FocusedComponent;
  setScrollToCard?: (index: number) => void;
  setFocusedComponent?: (component: FocusedComponent) => void;
};

export enum FocusedComponentType {
  SSO = "SSO",
  BRAND = "BRAND",
  PRIMARY_BUTTON = "PRIMARY_BUTTON",
  BANNER = "BANNER",
  TITLE_AND_SUBTITLE = "TITLE_AND_SUBTITLE",
  NONE = "NONE",
  CARD = "CARD",
  CARDS = "CARDS",

  // post login components and sections
  POST_LOGIN_SSO = "POST_LOGIN_SSO",
  POST_LOGIN_PRIMARY_BUTTON = "POST_LOGIN_PRIMARY_BUTTON",
  POST_LOGIN_TITLE_AND_SUBTITLE = "POST_LOGIN_TITLE_AND_SUBTITLE",
  POST_LOGIN_DISCOUNT_CODE = "POST_LOGIN_DISCOUNT_CODE",
}

export type FocusedComponent = {
  type: FocusedComponentType;
  cardIndex?: number;
  via: FocusedVia;
  cardStyleType?: cardStyleType;
};

export enum cardStyleType {
  BACKGROUND_COLOR = "BACKGROUND_COLOR",
  TEXT_COLOR = "TEXT_COLOR",
  BORDER_COLOR = "BORDER_COLOR",
  BORDER_WIDTH = "BORDER_WIDTH",
  BORDER_RADIUS = "BORDER_RADIUS",
}

export enum FocusedVia {
  POPUP = "POPUP",
  CONFIG = "CONFIG",
  NONE = "NONE",
}
